import { useStoreState } from 'easy-peasy';
import { useMemo } from 'react';
import { useMutation, useInfiniteQuery } from 'react-query';

import { useReportScope } from '@ge/feat-reporting/hooks/use-report-scope';
import { AttributeGroups, DateTimeFormats, EntityType, QueryKey } from '@ge/models/constants';
import { Config } from '@ge/shared/data-hooks';
import { useTableFilter } from '@ge/shared/data-hooks/use-table-filter';
import { sorter } from '@ge/util/metric-sorter';

import * as reportsService from '../services';

const dayjs = require('dayjs');

export const useSentReportListTemplate = ({
  filters,
  search,
  sortMetric,
  sortDirection,
  pageSize = 2000,
}) => {
  // use below code for site specific reports
  // this logic is to handle for reports for individual sites without service groups
  const sitesForView = useStoreState((state) => state.sites.sitesForView);
  const siteIds = sitesForView
    ?.filter(({ serviceGroups, id }) => {
      if (!serviceGroups?.length) {
        return id;
      }
    })
    .map((site) => site.id);

  const siteIdsList = siteIds?.length <= 50 ? siteIds.join(',') : null;

  const serviceGroupsSites = useStoreState((state) => state.sites.getViewServiceGroupsSites);
  const serviceGroups = serviceGroupsSites.map((group) => group.id);

  let scope = serviceGroups?.length <= 50 ? serviceGroups.join(',') : null;
  let scopeType = AttributeGroups.SERVICE_GROUP;
  if (siteIds?.length && scope != null && scope != '') {
    scopeType = scopeType + ',' + AttributeGroups.SITES;
    scope = scope + ',' + siteIdsList;
  } else if (siteIdsList != '') {
    scopeType = AttributeGroups.SITES;
    scope = siteIdsList;
  }
  const {
    data: allReports,
    fetchNextPage,
    hasNextPage,
    ...queryRest
  } = useInfiniteQuery(
    [QueryKey.SENT_REPORT, scope],
    ({ pageParam = 0 }) => {
      return reportsService.fetchSentReportListData({
        sortMetric: 'dateSent',
        sortDirection,
        pageIdx: pageParam,
        pageSize,
        scopeType,
        scope,
      });
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage?.pagination?.currentPageIdx < lastPage?.pagination?.totalPages - 1)
          return lastPage.pagination.currentPageIdx + 1;
        return false;
      },
      ...Config.EXECUTE_ONCE,
      cacheTime: 60,
      enabled: true,
    },
  );

  const mappedReports = useMemo(
    () => allReports?.pages?.flatMap((page) => page?.reports?.map((report) => report)),
    [allReports],
  );

  const updatedReports = mappedReports?.map((item) => {
    return {
      ...item,
      epochDateSent: item?.dateSent ? new Date(item?.dateSent).getTime() : '',
      epochStartDate: item?.startDate ? new Date(item?.startDate).getTime() : '',
    };
  });

  const {
    data: filteredData,
    filterValues,
    search: filterSearch,
  } = useTableFilter({
    data: updatedReports,
    filters,
    search,
  });

  return {
    data: filteredData?.sort(sorter(sortMetric, sortDirection)),
    allReports,
    filters: filterValues,
    fetchNextPage,
    hasNextPage,
    search: filterSearch,
    ...queryRest,
  };
};

export const useCreateReport = ({ onSuccess, onError }) => {
  const { endDate, startDate } = useReportScope();

  const { mutate } = useMutation(
    ({ reportState, distributionList, attachments, reportFile, sendMail = false }) => {
      const reportParameters = reportState ?? {};

      const json = {
        name: reportParameters.name,
        templateId: reportParameters.id,
        reportType: reportParameters.templateType,
        scopeType: reportParameters.scopeType,
        scope:
          reportParameters.scopeType === EntityType.CONTRACT
            ? reportParameters?.scope?.contractName
            : reportParameters.scope,
        distributionList,
        dateSent: dayjs().format(DateTimeFormats.ENDPOINT_PARAM),
        startDate: dayjs(startDate).format(DateTimeFormats.ENDPOINT_PARAM),
        endDate: dayjs(endDate).format(DateTimeFormats.ENDPOINT_PARAM),
        sendMail,
      };

      return reportsService.postNewReport(json, attachments, reportFile);
    },
    {
      onSuccess: (data) => onSuccess(data),
      onError: (error) => onError(error),
    },
  );

  return { mutate };
};

export const useSendReport = ({ onSuccess, onError }) => {
  const { mutate } = useMutation(
    ({ reportId, distributionList }) =>
      !reportId || !distributionList || distributionList.length < 1
        ? () => {}
        : reportsService.sendReport(reportId, distributionList),
    {
      onSuccess: (data) => onSuccess(data),
      onError: (error) => onError(error),
    },
  );
  return { mutate };
};
